import apiClient, { requestWorkspaceId } from './apiClient';
import type {
  TenantTheme,
  TenantContent,
  TenantConfig,
  TenantPreferences,
  HeaderFooterConfig,
} from '@/types/tenant.types';

/**
 * Tenant API service.
 * Mirrors Flutter GenericHelpers tenant config methods:
 *   getWorkspaceId(), getTenantThemeSetting(), getTenantContent(),
 *   getTenantSupportedLanguages(), getTenantLanguagesContants(),
 *   getTenantPreferences(), getMobilePreferences(), getTenantHeaderFooterConfig()
 *
 * Only the workspaceId lookup goes through Flutter's HttpInterCeptor; the config calls
 * use plain http, so they pass skipErrorInterceptor.
 */

/**
 * GET /v1/p/workspaceId?domain={hostname}
 * Mirrors Flutter IWebHomeRepository.getWorkSpaceId() — the interceptor sends it without headers.
 */
export async function fetchWorkspaceId(domain: string): Promise<string> {
  return requestWorkspaceId(domain);
}

/**
 * GET /v1/p/workspace/theme/{workspaceId}
 * Mirrors Flutter GenericHelpers.getTenantThemeSetting()
 */
export async function fetchTenantTheme(workspaceId: string): Promise<TenantTheme> {
  const res = await apiClient.get<TenantTheme>(`v1/p/workspace/theme/${workspaceId}`, {
    noAuth: true,
    skipErrorInterceptor: true,
  });
  return res.data;
}

/**
 * GET /v1/p/workspace/static-content/{workspaceId}
 * Mirrors Flutter GenericHelpers.getTenantContent()
 */
export async function fetchTenantContent(workspaceId: string): Promise<TenantContent> {
  const res = await apiClient.get<TenantContent>(
    `v1/p/workspace/static-content/${workspaceId}`,
    { noAuth: true, skipErrorInterceptor: true },
  );
  return res.data;
}

/**
 * GET /v1/p/language/constants with language: 'config'
 * Mirrors Flutter GenericHelpers.getTenantSupportedLanguages()
 */
export async function fetchTenantConfig(): Promise<TenantConfig> {
  const res = await apiClient.get<TenantConfig>('v1/p/language/constants', {
    noAuth: true,
    skipErrorInterceptor: true,
    headers: { language: 'config' },
  });
  return res.data;
}

export interface LanguageStringsResponse {
  strings: Record<string, string>;
  /** False when the request went out without a WorkspaceId header (workspace unresolved) */
  hadWorkspaceId: boolean;
}

/**
 * GET /v1/p/language/constants with language: {locale}
 * Mirrors Flutter GenericHelpers.getTenantLanguagesContants()
 */
export async function fetchLanguageStrings(locale: string): Promise<LanguageStringsResponse> {
  const res = await apiClient.get<Record<string, string>>(
    'v1/p/language/constants',
    { noAuth: true, skipErrorInterceptor: true, headers: { language: locale } },
  );
  return {
    strings: res.data,
    hadWorkspaceId: !!res.config.headers?.get?.('WorkspaceId'),
  };
}

/**
 * GET /v1/p/language/constants with language: 'tenantPreferences'
 * Mirrors Flutter GenericHelpers.getTenantPreferences()
 */
export async function fetchTenantPreferences(): Promise<TenantPreferences> {
  const res = await apiClient.get<TenantPreferences>('v1/p/language/constants', {
    noAuth: true,
    skipErrorInterceptor: true,
    headers: { language: 'tenantPreferences' },
  });
  return res.data;
}

/**
 * GET /v1/p/language/constants with language: 'mobilePreferences'
 * Mirrors Flutter GenericHelpers.getMobilePreferences()
 */
export async function fetchMobilePreferences(): Promise<Record<string, unknown>> {
  const res = await apiClient.get<Record<string, unknown>>(
    'v1/p/language/constants',
    { noAuth: true, skipErrorInterceptor: true, headers: { language: 'mobilePreferences' } },
  );
  return res.data;
}

/**
 * GET /v1/p/language/constants with language: 'headerFooterConfig'
 * Mirrors Flutter GenericHelpers.getTenantHeaderFooterConfig()
 */
export async function fetchHeaderFooterConfig(): Promise<HeaderFooterConfig> {
  const res = await apiClient.get<HeaderFooterConfig>('v1/p/language/constants', {
    noAuth: true,
    skipErrorInterceptor: true,
    headers: { language: 'headerFooterConfig' },
  });
  return res.data;
}
